import {
    DialogContent,
    TextField,
    Button,
    useTheme,
} from "@mui/material";
import { useState } from "react";
import TemplateClient, { Template, TemplateErrors } from "../apiClients/TemplateClient";
import { useErrorDisplay } from "../components/ErrorDisplay"
import { GetUpsertIcon, IsEmpty } from "../Helpers";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { UpsertAction } from "../Enums";
import { ProcessType } from "../models/dtos";
import PositionAutocompleteControl from "../components/PositionAutocompleteControl";
import { I18N } from "../I18N";
import { MobileFormStack, ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";

const newTemplate = {
    Name: "",
    TemplateRevisionId: "",
    Revision: 1,
    PositionId: '',
    ProcessType: ProcessType.Evaluate,
    IsActive: false,
    Id: null,
    HasTemplate: false,
    IsDeleted: false,
    LastUpdateDate: ""
}

export interface TemplateUpsertProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    templateId?: string;
    inTemplate?: Template;
    onChange?: () => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    icon?: JSX.Element;
}

const TemplateUpsertPopup = ({ mode, templateId, inTemplate, onChange = async () => { }, onAdd = async () => { }, icon = GetUpsertIcon(mode), ...btnProps }: TemplateUpsertProps) => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();

    const [template, setTemplate] = useState<Template>(newTemplate);
    const disableSave = IsEmpty(template?.Name) || IsEmpty(template?.PositionId);

    const title = (mode === UpsertAction.Create) ? 'Create Template' : 'Rename Template';

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                await createTemplate();
            }
            else if (mode === UpsertAction.Update) {
                await new TemplateClient().Update(templateId, template.Name);
                await onChange();
            }

        } catch (error) {
            setError(error, (mode === UpsertAction.Create) ? TemplateErrors.Create : TemplateErrors.Update);
        }
        finally {
            setBusy(false);
            setOpen(false);
        }
    };

    const createTemplate = async () => {
        let ct = {
            Process: template.ProcessType,
            PositionId: template.PositionId,
            Name: template.Name
        };

        let id = await new TemplateClient().Create(ct);
        await onAdd(id);
    }

    const handleInputChange = e => {
        const { name, value } = e.target
        setTemplate({ ...template, [name]: value })
    };

    const handleUserSelect = (positionId) => {
        setTemplate({ ...template, PositionId: positionId })
    }

    const handleOpen = async () => {
        setOpen(true);
        await loadData();
    };

    const loadData = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                setTemplate(newTemplate);
            }
            else if (mode === UpsertAction.Update) {
                let t = await new TemplateClient().GetSingle(templateId);
                setTemplate(t);
            }

        } catch (error) {
            setError(error, TemplateErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    return <>
        <ErrorDisplay />
        <IconButtonEx disabled={busy} hoverContent={title} onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog aria-labelledby="template-upsert-title" open={open} fullWidth tabletFullScreen="never" PaperProps={{ sx: { overflow: 'hidden', '& > div': { flex: '1 1 auto', minHeight: 0, height: 'auto !important', display: 'flex', flexDirection: 'column' } } }}>
            <BusySpinner busy={busy}>
                <ResponsiveDialogHeader id="template-upsert-title" title={title} />
                <DialogContent dividers sx={{ overflowY: 'auto' }}>
                    <MobileFormStack tabletColumns={2} desktopColumns={1}>
                    <TextField fullWidth
                        name="Name"
                        autoFocus
                        label={I18N.Translate('Name')}
                        error={IsEmpty(template?.Name)}
                        size='small'
                        variant="outlined"
                        value={template?.Name}
                        onChange={handleInputChange} />

                    {mode === UpsertAction.Create &&
                        <PositionAutocompleteControl positionId={template?.PositionId} onUserSelect={handleUserSelect} />}

                    {/* <TextField fullWidth select
                        label={I18N.Translate('Process Type')}
                        id="processType-select"
                        name="ProcessType"
                        error={IsEmpty(template?.ProcessType)}
                        value={template?.ProcessType ?? ProcessType.Evaluate}
                        onChange={handleInputChange}>
                        <MenuItem value={ProcessType.Evaluate}>{ProcessType[ProcessType.Evaluate]}</MenuItem>
                        <MenuItem value={ProcessType.Interview}>{ProcessType[ProcessType.Interview]}</MenuItem>
                    </TextField> */}
                    </MobileFormStack>
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                    <Button onClick={() => setOpen(false)} variant='text' color='inherit'>Cancel</Button>
                    <Button disabled={disableSave} onClick={handleSubmit} variant='outlined' color='primary'>Submit</Button>
                </StickyDialogActions>
            </BusySpinner>
        </ResponsiveDialog>
    </>;
}

export default TemplateUpsertPopup;
